// File: frontend/src/components/PageHeader.jsx

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';

/**
 * Universal PageHeader component providing consistent back buttons,
 * dynamic breadcrumb navigation, title/subtitle, and action button slots.
 */
const PageHeader = ({
  title,
  subtitle,
  breadcrumbs = [],
  backUrl,
  showBack = true,
  children,
  className = ''
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backUrl) {
      navigate(backUrl);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={`space-y-1.5 mb-5 print:hidden ${className}`}>
      {/* Breadcrumb Navigation Trail */}
      {breadcrumbs.length > 0 && (
        <nav className="flex items-center space-x-1.5 text-xs text-slate-500 font-medium overflow-x-auto pb-0.5 no-scrollbar">
          <Link
            to="/dashboard/admin"
            className="hover:text-purple-600 transition flex items-center gap-1 shrink-0"
          >
            <span>🏠</span>
            <span>Dashboard</span>
          </Link>

          {breadcrumbs.map((crumb, idx) => (
            <React.Fragment key={idx}>
              <span className="text-slate-300 shrink-0">/</span>
              {crumb.path ? (
                <Link
                  to={crumb.path}
                  className="hover:text-purple-600 transition shrink-0 truncate max-w-[150px] sm:max-w-none"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-slate-700 font-semibold shrink-0 truncate max-w-[180px] sm:max-w-none">
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Main Header Bar (Back Button + Title + Action Slots) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3.5 sm:p-4 rounded-xl border border-purple-100/80 shadow-xs">
        <div className="flex items-center space-x-3 min-w-0">
          {/* Back Button */}
          {showBack && (
            <button
              type="button"
              onClick={handleBack}
              className="p-1.5 sm:p-2 rounded-lg bg-slate-100 hover:bg-purple-50 text-slate-600 hover:text-purple-700 border border-slate-200 hover:border-purple-200 transition shadow-xs group shrink-0"
              title="Go Back to Previous Page"
            >
              <svg
                className="w-4 h-4 transition-transform group-hover:-translate-x-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
          )}

          {/* Title & Subtitle */}
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-lg font-bold text-slate-800 tracking-tight truncate leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-xs text-slate-500 mt-0.5 truncate">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Action Button Slots */}
        {children && (
          <div className="flex items-center flex-wrap gap-2 shrink-0 self-end sm:self-auto">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
